export function serializeDeal(deal, fields = {}) {
  const serialized = { ...deal };

  serializeCommissions(deal, serialized);
  serializeContactRoles(deal, serialized);
  serializeCustomFields(deal, fields, serialized);
  serializeDealUsers(deal, serialized);
  serializeLeaseTerm(deal, serialized);
  serializeLeaseTerms(deal, serialized);
  serializeListing(deal, serialized);
  serializeTransactionValue(deal, serialized);
  serializeTransactionSideDetails(deal, serialized);

  return serialized;
}

function serializeContactRoles(deal, serialized) {
  delete serialized.contactRoles;
  if (deal.contactRoles && deal.contactRoles.length) {
    serialized.contactRolesAttributes = [];
    for (const contactRole of deal.contactRoles) {
      const attributes = {
        id: contactRole.id,
        employerContactId: contactRole.employer?.id,
        employerCrmContactId: contactRole.employer?.crmContactId,
        employerCrmContactType: contactRole.employer?.crmContactType,
        role: contactRole.role,
        _destroy: contactRole._destroy,
      };

      if (contactRole.contact) {
        attributes.contactId = contactRole.contact.id;
        attributes.crmContactId = contactRole.contact.crmContactId;
        attributes.crmContactType = contactRole.contact.crmContactType;
      } else {
        attributes._destroy = true;
      }

      if (contactRole.id || contactRole.contact) {
        serialized.contactRolesAttributes.push(attributes);
      }
    }
  }
}

function serializeDealUsers(deal, serialized) {
  delete serialized.dealUsers;
  if (deal.dealUsers) {
    serialized.dealUsersAttributes = [];
    for (const dealUser of deal.dealUsers) {
      const attributes = {
        userId: dealUser.userId,
        _destroy: dealUser._destroy,
        transactionSide: dealUser.transactionSide
      };

      if (dealUser.id) {
        attributes.id = dealUser.id;
      }

      if (!dealUser.userId || dealUser.userId === -1) {
        attributes._destroy = true;
      }

      if (dealUser.id || dealUser.userId) {
        serialized.dealUsersAttributes.push(attributes);
      }
    }
  }
}

function serializeLeaseTerm(deal, serialized) {
  delete serialized.leaseTerm;
  if (deal.leaseTerm != null) {
    let leaseTermMonths = deal.leaseTerm;
    if (deal.leaseTermUnits === 'years') {
      leaseTermMonths *= 12;
    }

    serialized.leaseTermMonths = leaseTermMonths;
  }
}

function serializeLeaseTerms(deal, serialized) {
  delete serialized.leaseTerms;
  if (deal.leaseTerms) {
    serialized.leaseTermsAttributes = deal.leaseTerms.map((term) => {
      const leaseTerm = { ...term };
      if (deal.hasTransactionSides) {
        serializeTransactionSideDetails(term, leaseTerm);
      }
      return leaseTerm;
    });
  }
}

function serializeListing(deal, serialized) {
  delete serialized.listing;
  if (deal.listing) {
    serialized.listingId = deal.listing.id;
  }
}

function serializeTransactionValue(deal, serialized) {
  if (serialized.transactionValue == null) {
    // This value is non-nullable in the database, but may be null here for deals without vouchers
    delete serialized.transactionValue;
  }
}

export function serializeCommissions(deal, serialized = {}) {
  delete serialized.commissions;
  if (deal.commissions && deal.commissions.length) {
    serialized.commissionsAttributes = [];
    for (const commission of deal.commissions) {
      const attributes = {
        id: commission.id,
        commissionPlanId: commission.commissionPlanId,
        grossAmount: commission.grossAmount,
        grossPercent: commission.grossPercent,
        grossUnit: commission.grossUnit,
        outside: commission.outside,
        transactionSide: commission.transactionSide,
        _destroy: commission._destroy,
      };

      if (deal.hasTransactionSides) {
        serializeTransactionSideDetails(commission, attributes);
      }

      if (!commission.id) {
        attributes.grossAmount = attributes.grossAmount || 0;
        attributes.grossPercent = attributes.grossPercent || 0;
        attributes.brokerSplitAmount = attributes.brokerSplitAmount || 0;
        attributes.brokerSplitPercent = attributes.brokerSplitPercent || 0;
      }

      if (commission.outside && commission.contact) {
        attributes.backofficeContactId = commission.contact.id || null;
        attributes.crmContactId = commission.contact.crmContactId;
        attributes.crmContactType = commission.contact.crmContactType;
      } else if (!commission.outside && commission.broker) {
        attributes.brokerId = commission.broker.id;
      } else {
        // The form doesn't know anything about commissions, so from its point of view, clearing a
        // broker or contact from an input should remove it (which means removing the commission).
        attributes._destroy = true;
      }

      if (commission.id || commission.broker || commission.contact) {
        serialized.commissionsAttributes.push(attributes);
      }
    }
  }

  return serialized;
}

function serializeTransactionSideDetails(obj, serialized) {
  delete serialized.transactionSideDetails;
  if (obj.transactionSideDetails?.length) {
    serialized.transactionSideDetailsAttributes = obj.transactionSideDetails;
  }
}

function serializeCustomFields(deal, fields, serialized) {
  // Converting custom field names to snake case can be unreliable if the snake case name has a number after
  // an underscore (e.g. c_custom_field_1). Use the field definitions to convert the camelCase keys to the actual
  // custom field names expected by the API.
  const customFields = Object.entries(fields).filter(([key, field]) => field.custom && deal.hasOwnProperty(key));
  customFields.forEach(([key, field]) => {
    const value = deal[key];
    delete serialized[key];
    serialized[field.name] = value;
  });
}
